import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const CATS = ["Development","Design","Writing","Video","Marketing","Sales","Support","Data","Product","Admin","AI"];

export default function SettingsModal({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    handle: user?.handle || "",
    is_public: !!user?.is_public,
    bio: user?.bio || "",
    twitter: user?.twitter || "",
    digest_freq: user?.digest_freq || "weekly",
    digest_categories: user?.digest_categories || [],
  });
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (user) setForm({
      handle: user.handle || "",
      is_public: !!user.is_public,
      bio: user.bio || "",
      twitter: user.twitter || "",
      digest_freq: user.digest_freq || "weekly",
      digest_categories: user.digest_categories || [],
    });
  }, [user, open]);

  const toggleCat = (c) => {
    setForm(f => ({...f, digest_categories: f.digest_categories.includes(c)
      ? f.digest_categories.filter(x => x !== c)
      : [...f.digest_categories, c]}));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/profile", form);
      await refresh();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const publicUrl = form.handle ? `${window.location.origin}/u/${form.handle}` : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="settings-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl">Settings</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div>
            <div className="caption mb-3">Public profile</div>
            <Label>Handle</Label>
            <div className="flex items-center gap-2">
              <span className="text-[var(--stone)] font-mono">@</span>
              <Input value={form.handle} onChange={e=>setForm({...form, handle: e.target.value.toLowerCase()})}
                placeholder="hustler_alex" pattern="[a-z0-9_]{3,20}" data-testid="settings-handle-input"/>
            </div>
            <Label className="mt-3 block">Bio</Label>
            <Textarea rows={2} maxLength={280} value={form.bio} onChange={e=>setForm({...form, bio: e.target.value})}
              placeholder="Building side hustles one dollar at a time." data-testid="settings-bio-input"/>
            <Label className="mt-3 block">Twitter/X handle</Label>
            <Input value={form.twitter} onChange={e=>setForm({...form, twitter: e.target.value})}
              placeholder="hustleralex" data-testid="settings-twitter-input"/>
            <div className="flex items-center justify-between mt-4 p-3 rounded-xl border border-[var(--border-warm)]">
              <div>
                <div className="font-semibold text-sm">Make my profile public</div>
                <div className="text-xs text-[var(--stone)]">Anyone with the link can view your earnings & wins</div>
              </div>
              <Switch checked={form.is_public} onCheckedChange={(v)=>setForm({...form, is_public: v})} data-testid="settings-public-switch"/>
            </div>
            {form.is_public && form.handle && (
              <div className="mt-3 p-3 rounded-xl bg-[var(--alabaster-2)] flex items-center justify-between gap-2 flex-wrap">
                <code className="text-xs font-mono break-all">{publicUrl}</code>
                <div className="flex gap-2">
                  <button type="button" onClick={()=>{navigator.clipboard.writeText(publicUrl); toast.success("Copied");}}
                    className="text-xs font-semibold text-[var(--forest)] flex items-center gap-1"><Copy size={12}/> Copy</button>
                  <Link to={`/u/${form.handle}`} target="_blank" className="text-xs font-semibold text-[var(--forest)] flex items-center gap-1">
                    View <ExternalLink size={12}/>
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[var(--border-warm)] pt-4">
            <div className="caption mb-3">Digest emails</div>
            <Label>Frequency</Label>
            <Select value={form.digest_freq} onValueChange={(v)=>setForm({...form, digest_freq: v})}>
              <SelectTrigger data-testid="settings-freq-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly (Sundays)</SelectItem>
                <SelectItem value="off">Off — no emails</SelectItem>
              </SelectContent>
            </Select>
            {form.digest_freq !== "off" && (
              <>
                <Label className="mt-3 block">Focus on these categories (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CATS.map(c => {
                    const on = form.digest_categories.includes(c);
                    return (
                      <button type="button" key={c} onClick={()=>toggleCat(c)} data-testid={`cat-chip-${c}`}
                        className={`tag-pill cursor-pointer transition ${on ? "!bg-[var(--forest)] !text-[var(--alabaster)] !border-[var(--forest)]" : ""}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-[var(--stone)] mt-2">
                  {form.digest_categories.length ? `Filtering gigs to: ${form.digest_categories.join(", ")}` : "All categories"}
                </div>
              </>
            )}
          </div>

          <button type="submit" disabled={saving} className="btn-primary w-full justify-center" data-testid="settings-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
